import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  EmptyState,
  Panel,
  PRESS,
  SegmentedControl,
  Select,
  cn,
  formatDate,
  formatDateTime,
  formatMonth,
  formatNumber,
  useToast,
  type RemoteOption,
  type RemoteSearch,
  type SegmentedOption,
} from 'escrowbuild-ui';

import { useReports, useT, type AppTranslationKey } from '@/i18n';
import { PROJECTS, SPEND_BY_MONTH, findProject } from '@/data/domain';
import type { ReportRecord } from '@/data/domain';

/* =============================================================================
   Reports — §13 and §16
   -----------------------------------------------------------------------------
   The point this screen has to make is that a report is produced FROM the
   platform's records, not assembled afterwards from screenshots. That is why
   nothing here is presented as already existing: the controls build a request,
   the register lists what the database already holds, and there is no fake page
   count or downloadable file anywhere on the page.
   ============================================================================= */

/** A cancellable pause, so a superseded search never resolves into a newer one. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * The one search factory this page needs. Every combobox here opens over a
 * fixed list, so the shape is the design system's own showcase pattern: honour
 * the signal, then filter case-insensitively on the label.
 */
function searchOver(options: RemoteOption[]): RemoteSearch {
  return async (term, signal) => {
    await pause(140, signal);
    const needle = term.trim().toLowerCase();
    const matched =
      needle === '' ? options : options.filter((option) => option.label.toLowerCase().includes(needle));
    return { options: matched, truncated: false };
  };
}

/* ---- Generate-a-report option lists -------------------------------------- */

/*
  The two lists' null options and the scopes are held here as VALUES; their
  labels are not.

  Every one of these lists is built at module scope, before any provider exists,
  so a label written here would be frozen in whatever language the module
  happened to load in — and these lists are also what the search comboboxes
  filter on, so a frozen label would go on matching the wrong words after a
  language switch. The value travels from here and the component label it.

  The null option matters because the DS `Select` has no clear affordance:
  re-selecting the first entry is how a filter is removed.
*/
const ALL_DEVELOPMENTS = '';
const WHOLE_PROJECT = 'whole';
const ALL_PERIODS = '';

const REPORT_KINDS: ReportRecord['kind'][] = ['period', 'phase', 'final'];

/** One map for both the scope of a request and the kind of a filed report —
 *  the same three words either way. */
const KIND_LABEL: Record<ReportRecord['kind'], AppTranslationKey> = {
  period: 'reports.kind.period',
  phase: 'reports.kind.phase',
  final: 'reports.kind.final',
};

interface ReportRequest {
  id: string;
  projectName: string;
  scope: string;
  period: string;
  requestedAt: string;
}

export function ReportsPage() {
  const { toast } = useToast();
  const t = useT();

  /* The register, in the reader's language: a report's title, its period and its
     scope are the record's own words. The period filter offers the periods this
     copy holds, so a chosen option still matches the rows it filters. */
  const reports = useReports();

  const allDevelopments = useMemo<RemoteOption>(
    () => ({ value: ALL_DEVELOPMENTS, label: t('reports.allDevelopments') }),
    [t],
  );

  const projectOptions = useMemo<RemoteOption[]>(
    () => [allDevelopments, ...PROJECTS.map((entry) => ({ value: entry.id, label: entry.name }))],
    [allDevelopments],
  );

  const projectSearch = useMemo(() => searchOver(projectOptions), [projectOptions]);

  /** "Whole project" leads the month list — the project's own lifetime rather
   *  than any single month. */
  const monthOptions = useMemo<RemoteOption[]>(
    () => [
      { value: WHOLE_PROJECT, label: t('reports.wholeProject') },
      ...SPEND_BY_MONTH.map((row) => ({ value: row.month, label: formatMonth(row.month) })),
    ],
    [t],
  );

  const monthSearch = useMemo(() => searchOver(monthOptions), [monthOptions]);

  const scopeOptions = useMemo<SegmentedOption[]>(
    () => REPORT_KINDS.map((kind) => ({ value: kind, label: t(KIND_LABEL[kind]) })),
    [t],
  );

  const kindFilterOptions = useMemo<SegmentedOption[]>(
    () => [{ value: 'all', label: t('common.all') }, ...scopeOptions],
    [scopeOptions, t],
  );

  /** Only the periods the register actually holds, so a filter can never offer a
   *  value that matches nothing. */
  const periodOptions = useMemo<RemoteOption[]>(
    () => [
      { value: ALL_PERIODS, label: t('reports.allPeriods') },
      ...[...new Set(reports.map((report) => report.period))]
        .sort((a, b) => a.localeCompare(b))
        .map((period) => ({ value: period, label: period })),
    ],
    [t],
  );

  const periodSearch = useMemo(() => searchOver(periodOptions), [periodOptions]);

  /*
    Only the chosen VALUE is state, never the chosen option: an option carries
    its label, and a label held in state would be the language it was picked in
    — the closed control would keep showing the previous language's words after
    a switch. The option is resolved from the list on every render instead.
  */
  const [projectValue, setProjectValue] = useState(ALL_DEVELOPMENTS);
  const [scope, setScope] = useState('');
  const [periodValue, setPeriodValue] = useState<string | null>(null);
  const [requests, setRequests] = useState<ReportRequest[]>([]);

  const [kind, setKind] = useState('all');
  const [developmentValue, setDevelopmentValue] = useState(ALL_DEVELOPMENTS);
  const [registerPeriodValue, setRegisterPeriodValue] = useState(ALL_PERIODS);

  const project =
    projectOptions.find((option) => option.value === projectValue) ?? allDevelopments;
  const period =
    periodValue === null
      ? null
      : (monthOptions.find((option) => option.value === periodValue) ?? null);
  const development =
    projectOptions.find((option) => option.value === developmentValue) ?? allDevelopments;
  const registerPeriod =
    periodOptions.find((option) => option.value === registerPeriodValue) ?? periodOptions[0];

  const canGenerate = project.value !== '' && scope !== '' && period !== null;

  const filtered = useMemo(
    () =>
      reports.filter((report) => {
        if (kind !== 'all' && report.kind !== kind) return false;
        if (developmentValue !== '' && report.projectId !== developmentValue) return false;
        if (registerPeriodValue !== '' && report.period !== registerPeriodValue) return false;
        return true;
      }),
    [kind, developmentValue, registerPeriodValue],
  );

  const clearFilters = () => {
    setKind('all');
    setDevelopmentValue(ALL_DEVELOPMENTS);
    setRegisterPeriodValue(ALL_PERIODS);
  };

  const onGenerate = () => {
    if (!canGenerate || !period) return;

    const scopeLabel = scopeOptions.find((option) => option.value === scope)?.label ?? scope;
    setRequests((current) => [
      {
        id: crypto.randomUUID(),
        projectName: project.label,
        scope: String(scopeLabel),
        period: period.label,
        requestedAt: new Date().toISOString(),
      },
      ...current,
    ]);

    toast({
      variant: 'success',
      title: t('reports.toast.requestedTitle'),
      description: `${project.label} · ${scopeLabel} · ${period.label}`,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('reports.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('reports.intro')}</p>
      </div>

      <Panel title={t('reports.generateTitle')} description={t('reports.generateDescription')}>
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Select
              label={t('reports.development')}
              placeholder={t('reports.searchDevelopments')}
              search={projectSearch}
              value={project}
              onValueChange={(option) => setProjectValue(option?.value ?? ALL_DEVELOPMENTS)}
              initialLoad
              minSearchLength={0}
            />

            <div className="flex flex-col gap-1.5">
              <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('reports.scope')}
              </span>
              <SegmentedControl
                label={t('reports.scopeLabel')}
                options={scopeOptions}
                value={scope}
                onValueChange={setScope}
                size="md"
              />
            </div>

            <Select
              label={t('reports.period')}
              placeholder={t('reports.searchPeriods')}
              search={monthSearch}
              value={period}
              onValueChange={(option) => setPeriodValue(option?.value ?? null)}
              initialLoad
              minSearchLength={0}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" disabled={!canGenerate} onClick={onGenerate}>
              {t('reports.generate')}
            </Button>
            <p className="text-xs text-muted">{t('reports.generateHint')}</p>
          </div>

          <div className="border-t border-line-subtle pt-4">
            <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('reports.requestedTitle')}
            </h3>
            {requests.length === 0 ? (
              <p className="mt-2 text-xs text-muted">{t('reports.requestedEmpty')}</p>
            ) : (
              <ul className="mt-2 flex list-none flex-col gap-1 p-0">
                {requests.map((request) => (
                  <li
                    key={request.id}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-line-subtle py-2 text-sm last:border-b-0"
                  >
                    <span className="text-body">
                      <span className="font-medium text-heading">{request.projectName}</span>
                      {` · ${request.scope} · ${request.period}`}
                    </span>
                    <span className="text-xs text-muted">
                      {t('reports.requestedAt', {
                        date: formatDateTime(request.requestedAt),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Panel>

      <Panel
        title={t('reports.registerTitle')}
        description={t('reports.registerDescription')}
        padding="none"
      >
        <div className="flex flex-col gap-3 border-b border-line-subtle px-4 py-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('reports.kind')}
              </span>
              <SegmentedControl
                label={t('reports.kindLabel')}
                options={kindFilterOptions}
                value={kind}
                onValueChange={setKind}
                size="md"
              />
            </div>

            <Select
              className="w-full sm:w-64"
              label={t('reports.development')}
              placeholder={t('reports.searchDevelopments')}
              search={projectSearch}
              value={development}
              onValueChange={(option) => setDevelopmentValue(option?.value ?? ALL_DEVELOPMENTS)}
              initialLoad
              minSearchLength={0}
            />

            <Select
              className="w-full sm:w-64"
              label={t('reports.period')}
              placeholder={t('reports.searchPeriods')}
              search={periodSearch}
              value={registerPeriod}
              onValueChange={(option) => setRegisterPeriodValue(option?.value ?? ALL_PERIODS)}
              initialLoad
              minSearchLength={0}
            />
          </div>

          <p role="status" className="text-xs text-body">
            {t('reports.showing', {
              shown: formatNumber(filtered.length),
              total: formatNumber(reports.length),
            })}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="px-4 py-6">
            <EmptyState
              variant="table"
              filtersActive
              onClearFilters={clearFilters}
              title={t('reports.emptyTitle')}
              description={t('reports.emptyBody')}
              action={
                <span className="text-xs text-muted">
                  {t('reports.heldInTotal', { count: reports.length })}
                </span>
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t('reports.caption')}</caption>
              <thead>
                <tr className="h-12 border-b-2 border-line-strong">
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.column.report')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.development')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.kind')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.period')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.scope')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.column.generated')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.column.by')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-right font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.column.pages')}
                  </th>
                  <th
                    scope="col"
                    className="bg-surface-alt px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted"
                  >
                    {t('reports.column.state')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((report) => {
                  const notGenerated = report.pages === 0;

                  return (
                    <tr key={report.id} className="h-12 border-b border-line-subtle hover:bg-surface-alt">
                      <td className="px-3 py-2 align-middle">
                        <Link
                          to={`/reports/${report.id}`}
                          className={cn(
                            'rounded-xs font-medium text-primary underline decoration-line-strong underline-offset-2',
                            'transition-colors duration-[var(--dur-fast)] hover:decoration-primary',
                            PRESS,
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                          )}
                        >
                          {report.title}
                        </Link>
                      </td>
                      <td className="px-3 py-2 align-middle text-body">
                        {findProject(report.projectId)?.name ?? '—'}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <Badge
                          variant="subtle"
                          tone={
                            report.kind === 'period'
                              ? 'info'
                              : report.kind === 'phase'
                                ? 'neutral'
                                : 'warning'
                          }
                        >
                          {t(KIND_LABEL[report.kind])}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 align-middle text-body">{report.period}</td>
                      <td className="px-3 py-2 align-middle text-body">{report.scope}</td>
                      <td className="px-3 py-2 align-middle text-body">
                        {notGenerated
                          ? t('reports.notGenerated')
                          : formatDate(report.generatedAt, { style: 'medium' })}
                      </td>
                      <td className="px-3 py-2 align-middle text-body">{report.generatedBy}</td>
                      <td className="px-3 py-2 align-middle text-right text-body tabular-nums">
                        {notGenerated ? '—' : formatNumber(report.pages)}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <Badge variant="subtle" tone={report.published ? 'success' : 'neutral'}>
                          {report.published
                            ? t('status.publication.published')
                            : t('status.publication.draft')}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="border-t border-line-subtle px-4 py-3 text-xs text-muted">
          {t('reports.footnote')}
        </p>
      </Panel>
    </div>
  );
}
